import React, { useState, useEffect } from 'react';
import { XMarkIcon, ClockIcon, UserIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';

const StatusChangeModal = ({ 
  isOpen, 
  onClose, 
  lead, 
  newStatus, 
  onConfirm, 
  statusHistory = [],
  viewHistoryOnly = false
}) => {
  const [remark, setRemark] = useState('');
  const [showHistory, setShowHistory] = useState(viewHistoryOnly);

  // Reset showHistory when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowHistory(viewHistoryOnly);
    }
  }, [isOpen, viewHistoryOnly]);

  if (!isOpen) return null;

  const isViewOnly = viewHistoryOnly || (lead?.status === newStatus);

  const handleConfirm = () => {
    if (!isViewOnly) {
      onConfirm(remark);
    }
    setRemark('');
  };

  const handleClose = () => {
    setRemark('');
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'qualified': 'bg-purple-100 text-purple-800',
      'quoted': 'bg-orange-100 text-orange-800',
      'quotated': 'bg-orange-100 text-orange-800',
      'negotiation': 'bg-indigo-100 text-indigo-800',
      'won': 'bg-green-100 text-green-800',
      'converted': 'bg-green-100 text-green-800',
      'lost': 'bg-red-100 text-red-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const history = lead?.statusHistory || statusHistory || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-4 ${isViewOnly ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isViewOnly && <ClockIcon className="h-5 w-5 text-white" />}
              <h3 className="text-lg font-semibold text-white">
                {isViewOnly ? 'Status History' : 'Change Status'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Lead Info */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="font-medium text-gray-900">{lead?.name}</div>
            <div className="text-sm text-gray-500">{lead?.email}</div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-gray-500">Current Status:</span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead?.status)}`}>
                {lead?.status || 'new'}
              </span>
            </div>
          </div>

          {/* Status Change Info - Only show if not view only */}
          {!isViewOnly && (
            <>
              <div className="mb-6">
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">From</div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(lead?.status)}`}>
                      {lead?.status || 'new'}
                    </span>
                  </div>
                  <div className="text-gray-400 text-xl">→</div>
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">To</div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(newStatus)}`}>
                      {newStatus}
                    </span>
                  </div>
                </div>
              </div>

              {/* Remark Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <ChatBubbleLeftIcon className="h-4 w-4 inline mr-1" />
                  Add Remark (Optional)
                </label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Enter reason for status change, notes, or any comments..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>
            </>
          )}

          {/* Status History Toggle - Only show if not view only mode */}
          {!isViewOnly && history.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                <ClockIcon className="h-4 w-4" />
                {showHistory ? 'Hide' : 'View'} Status History ({history.length})
              </button>
            </div>
          )}

          {/* Status History - Always show in view only mode */}
          {(showHistory || isViewOnly) && history.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <ClockIcon className="h-4 w-4" />
                  Status History ({history.length} changes)
                </h4>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {history.slice().reverse().map((entry, idx) => (
                  <div key={idx} className="px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-medium text-sm">
                        {(entry.changedByName || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(entry.fromStatus)}`}>
                            {entry.fromStatus || 'new'}
                          </span>
                          <span className="text-gray-400 text-sm">→</span>
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(entry.toStatus)}`}>
                            {entry.toStatus}
                          </span>
                        </div>
                        {entry.remark && (
                          <div className="bg-yellow-50 border-l-2 border-yellow-400 px-3 py-2 rounded-r mt-2">
                            <p className="text-sm text-gray-700">"{entry.remark}"</p>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <UserIcon className="h-3 w-3" />
                          <span className="font-medium text-gray-700">{entry.changedByName || 'Unknown'}</span>
                          <span>•</span>
                          <span>{formatDate(entry.changedAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No History Message */}
          {isViewOnly && history.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <ClockIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No status changes recorded yet</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {isViewOnly ? 'Close' : 'Cancel'}
          </button>
          {!isViewOnly && (
            <button
              onClick={handleConfirm}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-medium"
            >
              Confirm Change
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatusChangeModal;
